import React from "react";
import { useParams } from "react-router-dom";
import { Alert, Skeleton } from "@mui/material";
import CancerConceptDetail from "catalog/components/CancerConceptDetail";
import QuestionDetail from "catalog/components/QuestionDetail";
import QuestionGroupDetail from "catalog/components/QuestionGroupDetail";
import useConceptDetails from "catalog/hooks/useConceptDetails";
import { useRegisterChartElements } from "catalog/utils/chart";
import EmptyState from "~/components/EmptyState";
import FlatCard from "~/components/FlatCard";
import DerivedGroupDetail from "./DerivedGroupDetail";
import { BreastCancerDetails } from "./cancers";

// Render custom detail pages for specific concept ids
const CustomCancerViews: Record<string, React.FC<any>> = {
  "45571518": BreastCancerDetails,
};

const LoadingSkeleton = () => (
  <>
    <Skeleton variant="rounded" height={100} sx={{ mt: 2 }} />
    <Skeleton variant="rounded" height={400} />
    <Skeleton variant="rounded" height={400} />
  </>
);

const ConceptDetail = () => {
  useRegisterChartElements();
  const { conceptId } = useParams();
  const { data: detailsResp, isLoading, error } = useConceptDetails(conceptId);
  const cancerData = detailsResp?.data?.site;
  const questionGroupData = detailsResp?.data?.question_group;
  const derivedGroupData = detailsResp?.data?.derived_group;
  const questionData = detailsResp?.data?.question;

  if (error) {
    if (error.status === 404) {
      return (
        <FlatCard>
          <EmptyState />
        </FlatCard>
      );
    }
    return (
      <Alert severity="error" variant="outlined">
        Unable to load concept details
      </Alert>
    );
  }

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (cancerData) {
    /**
     * Override the default view with custom views for specific cancers.
     */
    const DetailView = CustomCancerViews?.[conceptId || ""] ?? CancerConceptDetail;
    return <DetailView data={cancerData} />;
  }

  if (questionGroupData) {
    return <QuestionGroupDetail data={questionGroupData} />;
  }

  if (derivedGroupData) {
    return <DerivedGroupDetail data={derivedGroupData} />;
  }

  if (questionData) {
    return <QuestionDetail showGroupLink data={questionData} />;
  }

  return null;
};

export default ConceptDetail;
